import React, { useState } from 'react';
import { CircularProgress, Grid } from '@material-ui/core';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMedia } from 'helpers';
import { googleOAuth, userSignUp } from 'api/authRequests';
import { DESIGN_CONFIG, STRING_CONFIG } from 'utils';
import { GoogleLogin } from '@react-oauth/google';
import { showToast } from 'store/platform/actions';
import { isToken, isTrialMode, showAuthModal, userDetails } from '../../store/auth/actions';
import SignUpInput from './Components/SignUpInput';
import Button from '../../ReusableComponents/Button';
import Text from '../../ReusableComponents/Text';
import Box from '../../ReusableComponents/Box';
import EmailInput from './Components/EmailInput';
import DatePickerInput from './Components/DatePickerInput';
import DropDown from './Components/Dropdown';
import UsernameInput from './Components/UsernameInput';
import { GA_EVENTS } from 'utils/analytics';

function SignUp({
  setAuthInterface,
  isModal,
  setUserEmail,
  setUserImage,
}) {
  const dispatch = useDispatch();

  const history = useHistory();

  const userInfos = {
    confirmPassword: '',
    dateOfBirth: '',
    email: '',
    firstName: '',
    gender: '',
    lastName: '',
    password: '',
    username: '',
  };

  const [user, setUser] = useState(userInfos);

  const authToken = useSelector((state) => state.auth.token);

  const [loading, setLoading] = useState(false);

  const smallDevice = useMedia('(max-width:450px)');

  const { email, username } = user;

  const registerValidation = Yup.object({
    firstName: Yup.string()
      .required(STRING_CONFIG.validation.userProfile.firstName.empty)
      .min(2, STRING_CONFIG.validation.userProfile.firstName.invalidLength)
      .max(50, STRING_CONFIG.validation.userProfile.firstName.invalidLength)
      .matches(
        /^[a-zA-Z-' ]+$/,
        STRING_CONFIG.validation.userProfile.firstName.policyFailed
      )
      .lowercase(),
    lastName: Yup.string()
      .required(STRING_CONFIG.validation.userProfile.lastName.empty)
      .min(2, STRING_CONFIG.validation.userProfile.lastName.invalidLength)
      .max(50, STRING_CONFIG.validation.userProfile.lastName.invalidLength)
      .matches(
        /^[a-zA-Z-' ]+$/,
        STRING_CONFIG.validation.userProfile.lastName.policyFailed
      )
      .lowercase(),
    password: Yup.string()
      .min(8, STRING_CONFIG.validation.userProfile.password.invalidLength)
      .max(64, STRING_CONFIG.validation.userProfile.password.invalidLength)
      .required(STRING_CONFIG.validation.userProfile.password.empty)
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,64}$/,
        STRING_CONFIG.validation.userProfile.password.policyFailed
      ),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref('password'), null],
        STRING_CONFIG.validation.userProfile.confirmPassword.passwordDoNotMatch
      )
      .required(STRING_CONFIG.validation.userProfile.confirmPassword.empty),
  });

  const registerSubmit = async (values, submitProp) => {
    try {
      setLoading(true);
      const userData = {
        dateOfBirth: user.dateOfBirth,
        email,
        firstname: values.firstName,
        gender: user.gender,
        lastname: values.lastName,
        password: values.password,
        username,
      };

      const { data } = await userSignUp(userData, authToken);
      if (data.success === true) {
        setLoading(false);

        submitProp.setSubmitting(false);

        submitProp.resetForm();

        dispatch(showAuthModal(false));

        history.push('/email-verification');
      }
    } catch (err) {
      setLoading(false);
      dispatch(showAuthModal(false));
      submitProp.setSubmitting(false);
      if (err.response || err.request) {
        dispatch(
          showToast(
            STRING_CONFIG.inPage.toastMessages.txtErrorSomethingWentWrong,
            'error',
            6000,
            false,
            true
          )
        );
      }
    }
  };

  const registerUserViaOauth = async (tokenId) => {
    const googleToken = {
      token: tokenId,
    };
    try {
      const { data } = await googleOAuth(googleToken, authToken);
      if (data.success === true && data.isNewUser) {
        setUserImage(data.image);

        setUserEmail(data.email);

        setAuthInterface(3);
      } else if (data.success === true) {

        const userDetailsObj = {
          dateOfBirth: data?.dateOfBirth,
          email: data.email,
          firstName: data.firstName,
          gender: data?.gender,
          image: data.image,
          isOAuth: data.isOAuth,
          lastName: data.lastName,
          phone: data?.phone,
          username: data.username,
        };

        dispatch(userDetails(userDetailsObj));

        dispatch(isToken(data.token));

        dispatch(showAuthModal(false));

        dispatch(isTrialMode(null));

      }
    } catch (err) {
      dispatch(showAuthModal(false));
      if (err.response) {
        if (err.response.status === 409) {
          dispatch(
            showToast(
              STRING_CONFIG.inPage.toastMessages.txtEmailAlreadyRegistered,
              'error',
              6000,
              false,
              true
            )
          );
        } else if (err.response.status === 401) {
          dispatch(
            showToast(
              STRING_CONFIG.inPage.toastMessages.txtInvalidAuthToken,
              'error',
              6000,
              false,
              true
            )
          );
        }
      } else if (err.request) {
        dispatch(
          showToast(
            STRING_CONFIG.inPage.toastMessages.txtErrorSomethingWentWrong,
            'error',
            6000,
            false,
            true
          )
        );
      }
    }
  };

  const responseGoogleSuccess = (response) => {
    if (response.credential) {
      registerUserViaOauth(response.credential);

      window.dataLayer.push({
        event: GA_EVENTS.SIGN_UP,
        method: 'Google'
      });
    }
  };

  const responseGoogleFailure = () => {
    dispatch(
      showToast(
        STRING_CONFIG.inPage.dialogMessages.txtOAuthError,
        'error',
        null,
        true,
        true
      )
    );
  };
  return (
    <Formik
      initialValues={userInfos}
      validationSchema={registerValidation}
      onSubmit={registerSubmit}
      validateOnMount
    >
      {(formik) => (
        <Form
          className='user-auth__sign-up'
          style={{
            background: `${DESIGN_CONFIG.color.neutral.neutral500}`,
            borderRadius: `${isModal ? '.5rem' : '.75rem'}`,
            margin: `${isModal ? 'auto' : 'auto'}`,
          }}
        >
          <Box display='flex' flexDirection='column'>
            <h1
              style={{
                fontSize: `${smallDevice ? '1.25rem' : DESIGN_CONFIG.typography.largeCards.webLgCardTitle.fontSize}`,
                fontWeight: `${smallDevice ? 600 : DESIGN_CONFIG.typography.largeCards.webLgCardTitle.fontWeight}`,
                margin: 0,
              }}
            >
              {STRING_CONFIG.inPage.signUp.txtTitle}
            </h1>
            <h3
              style={{
                color: `${DESIGN_CONFIG.color.text.text200}`,
                fontSize: `${smallDevice ? '.875rem' : DESIGN_CONFIG.typography.largeCards.webLgCardSubtitle.fontSize}`,
                fontWeight: `${smallDevice ? 400 : DESIGN_CONFIG.typography.largeCards.webLgCardSubtitle.fontWeight}`,
                marginBottom: 0,
              }}
            >
              {STRING_CONFIG.inPage.signUp.txtSubtext}
              <button
                type='button'
                onClick={() => setAuthInterface(1)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: `${DESIGN_CONFIG.color.primary.primaryMain}`,
                  cursor: 'pointer',
                  fontSize: `${smallDevice ? '1rem' : DESIGN_CONFIG.typography.largeCards.webLgCardSubtitle.fontSize}`,
                  fontWeight: `${smallDevice ? 400 : DESIGN_CONFIG.typography.largeCards.webLgCardSubtitle.fontSize}`,
                }}
              >
                {STRING_CONFIG.inPage.common.txtSignIn}
              </button>
            </h3>
            <Box display='flex' justifyContent='center' marginTop='1rem'>
              <GoogleLogin
                context='signup'
                onError={responseGoogleFailure}
                onSuccess={responseGoogleSuccess}
                width={!smallDevice && '420px'}
              />
            </Box>
            <Box display='flex' gap='0.688rem' marginTop='1.375rem'>
              <Box
                background={DESIGN_CONFIG.color.neutral.neutral200}
                h='1px'
                w='100%'
              />
              <p
                style={{
                  bottom: '0.688rem',
                  color: `${DESIGN_CONFIG.color.neutral.neutral200}`,
                  fontSize: `${DESIGN_CONFIG.typography.common.webLabel.fontSize}`,
                  fontWeight: `${DESIGN_CONFIG.typography.common.webLabel.fontWeight}`,
                  margin: 0,
                  position: 'relative',
                }}
              >
                {STRING_CONFIG.inPage.common.txtOr}
              </p>
              <Box
                w='100%'
                h='1px'
                background={DESIGN_CONFIG.color.neutral.neutral200}
              />
            </Box>
            <Box>
              <Grid container>
                <Grid item xs={12}>
                  <Box>
                    <label
                      htmlFor='firstName'
                      style={{ marginBottom: 8, display: 'flex' }}
                    >
                      <Text
                        fontSize={
                          DESIGN_CONFIG.typography.common.webLabel.fontSize
                        }
                        fontWeight={
                          DESIGN_CONFIG.typography.common.webLabel.fontWeight
                        }
                        margin={0}
                        textColor={DESIGN_CONFIG.color.text.text100}
                      >
                        {STRING_CONFIG.inPage.userProfile.lblFirstName}
                      </Text>
                    </label>
                    <Box display='flex' w='100%'>
                      <Box w='100%'>
                        <Field name='firstName'>
                          {(props) => {
                            const { field, meta } = props;
                            return (
                              <SignUpInput
                                field={field}
                                id='firstName'
                                meta={meta}
                                placeHolder={
                                  STRING_CONFIG.inPage.userProfile.plHFirstName
                                }
                                type='text'
                              />
                            );
                          }}
                        </Field>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    display='flex'
                    flexDirection='column'
                    marginTop='0.313rem'
                  >
                    <label
                      htmlFor='text'
                      style={{ marginBottom: 8, display: 'flex' }}
                    >
                      <Text
                        fontSize={
                          DESIGN_CONFIG.typography.common.webLabel.fontSize
                        }
                        fontWeight={
                          DESIGN_CONFIG.typography.common.webLabel.fontWeight
                        }
                        margin={0}
                        textColor={DESIGN_CONFIG.color.text.text100}
                      >
                        {STRING_CONFIG.inPage.userProfile.lblLastName}
                      </Text>
                    </label>
                    <Box display='flex'>
                      <Box display='flex' flexDirection='column' w='100%'>
                        <Field name='lastName'>
                          {(props) => {
                            const { field, meta } = props;
                            return (
                              <SignUpInput
                                field={field}
                                id='lastName'
                                meta={meta}
                                placeHolder={
                                  STRING_CONFIG.inPage.userProfile.plHLastName
                                }
                                type='text'
                              />
                            );
                          }}
                        </Field>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Box display='flex' maxW='580px' w='100%'>
                    <Box
                      display='flex'
                      flexDirection='column'
                      marginTop='0.313rem'
                      w='100%'
                    >
                      <Box
                        display='flex'
                        justifyContent='space-between'
                        marginBottom='.5rem'
                        marginRight='.25rem'
                      >
                        <Box display='flex'>
                          <Text
                            fontSize={
                              DESIGN_CONFIG.typography.common.webLabel.fontSize
                            }
                            fontWeight={
                              DESIGN_CONFIG.typography.common.webLabel
                                .fontWeight
                            }
                            margin={0}
                            textColor={DESIGN_CONFIG.color.text.text100}
                          >
                            {STRING_CONFIG.inPage.userProfile.lblUsername}
                          </Text>
                        </Box>
                      </Box>
                      <Box>
                        <UsernameInput
                          id='Username'
                          placeHolder={
                            STRING_CONFIG.inPage.userProfile.plHUsername
                          }
                          setUser={setUser}
                          type='text'
                          user={user}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Box display='flex' maxW='580px' w='100%'>
                    <Box
                      display='flex'
                      flexDirection='column'
                      marginTop='0.313rem'
                      w='100%'
                    >
                      <Box
                        display='flex'
                        justifyContent='space-between'
                        marginBottom='.5rem'
                        marginRight='.25rem'
                      >
                        <Box display='flex'>
                          <Text
                            fontSize={
                              DESIGN_CONFIG.typography.common.webLabel.fontSize
                            }
                            fontWeight={
                              DESIGN_CONFIG.typography.common.webLabel
                                .fontWeight
                            }
                            margin={0}
                            textColor={DESIGN_CONFIG.color.text.text100}
                          >
                            {STRING_CONFIG.inPage.userProfile.lblEmail}
                          </Text>
                        </Box>
                      </Box>
                      <Box>
                        <EmailInput
                          id='email'
                          placeHolder={
                            STRING_CONFIG.inPage.userProfile.plHEmail
                          }
                          setUser={setUser}
                          type='email'
                          user={user}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Box
                    display='flex'
                    flexDirection='column'
                    marginTop='0.313rem'
                  >
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      marginBottom='.5rem'
                      marginRight='.25rem'
                    >
                      <Box display='flex'>
                        <Text
                          fontSize={
                            DESIGN_CONFIG.typography.common.webLabel.fontSize
                          }
                          fontWeight={
                            DESIGN_CONFIG.typography.common.webLabel.fontWeight
                          }
                          margin={0}
                          textColor={DESIGN_CONFIG.color.text.text100}
                        >
                          {STRING_CONFIG.inPage.userProfile.lblPassword}
                        </Text>
                      </Box>
                    </Box>
                    <Box position='relative'>
                      <Field name='password'>
                        {(props) => {
                          const { field, meta } = props;
                          return (
                            <SignUpInput
                              field={field}
                              id='password'
                              meta={meta}
                              placeHolder={
                                STRING_CONFIG.inPage.userProfile.plHPassword
                              }
                              type='password'
                            />
                          );
                        }}
                      </Field>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    display='flex'
                    flexDirection='column'
                    marginBottom='.5rem'
                    marginTop='0.313rem'
                  >
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      marginBottom='.5rem'
                      marginRight='.25rem'
                    >
                      <Box display='flex'>
                        <Text
                          fontSize={
                            DESIGN_CONFIG.typography.common.webLabel.fontSize
                          }
                          fontWeight={
                            DESIGN_CONFIG.typography.common.webLabel.fontWeight
                          }
                          margin={0}
                          textColor={DESIGN_CONFIG.color.text.text100}
                        >
                          {STRING_CONFIG.inPage.userProfile.lblConfirmPassword}
                        </Text>
                      </Box>
                    </Box>
                    <Box position='relative'>
                      <Field name='confirmPassword'>
                        {(props) => {
                          const { field, meta } = props;
                          return (
                            <SignUpInput
                              field={field}
                              id='confirmPassword'
                              meta={meta}
                              placeHolder={
                                STRING_CONFIG.inPage.userProfile
                                  .plHConfirmPassword
                              }
                              type='password'
                            />
                          );
                        }}
                      </Field>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box display='flex' gap='1rem' justifyContent='space-between'>
            <Box flex={1}>
              <Box alignItems='center' display='flex' gap='.5rem'>
                <Text
                  fontSize={DESIGN_CONFIG.typography.common.webLabel.fontSize}
                  fontWeight={
                    DESIGN_CONFIG.typography.common.webLabel.fontWeight
                  }
                  marginBottom='.5rem'
                  textColor={DESIGN_CONFIG.color.text.text200}
                >
                  {STRING_CONFIG.inPage.userProfile.lblGender}
                </Text>
                <span
                  style={{
                    background: `${DESIGN_CONFIG.color.neutral.neutral50}`,
                    borderRadius: '1111px',
                    fontSize: `${DESIGN_CONFIG.typography.common.webLabel.fontSize}`,
                    fontWeight: `${DESIGN_CONFIG.typography.common.webLabel.fontWeight}`,
                    marginBottom: '.5rem',
                    padding: '0 .5rem',
                    textColor: `${DESIGN_CONFIG.color.text.text200}`,
                  }}
                >
                  {STRING_CONFIG.inPage.common.txtOptional}
                </span>
              </Box>
              <DropDown setUser={setUser} />
            </Box>
            <Box flex={1}>
              <Box alignItems='center' display='flex' gap='.5rem'>
                <Text
                  fontSize={DESIGN_CONFIG.typography.common.webLabel.fontSize}
                  fontWeight={
                    DESIGN_CONFIG.typography.common.webLabel.fontWeight
                  }
                  marginBottom='.5rem'
                  textColor={DESIGN_CONFIG.color.text.text200}
                >
                  {STRING_CONFIG.inPage.userProfile.lblDateOfBirth}
                </Text>
                <span
                  style={{
                    background: `${DESIGN_CONFIG.color.neutral.neutral50}`,
                    borderRadius: '1111px',
                    fontSize: `${DESIGN_CONFIG.typography.common.webLabel.fontSize}`,
                    fontWeight: `${DESIGN_CONFIG.typography.common.webLabel.fontWeight}`,
                    marginBottom: '.5rem',
                    padding: '0 .5rem',
                    textColor: `${DESIGN_CONFIG.color.text.text200}`,
                  }}
                >
                  {STRING_CONFIG.inPage.common.txtOptional}
                </span>
              </Box>
              <DatePickerInput setUser={setUser} />
            </Box>
          </Box>
          <Button
            alignItems='center'
            aria-disabled={
              !email || !username || !formik.isValid || formik.isSubmitting
            }
            background={DESIGN_CONFIG.color.primary.primaryMain}
            border='none'
            borderRadius='6px'
            cursor='pointer'
            disabled={
              !email || !username || !formik.isValid || formik.isSubmitting
            }
            display='flex'
            justifyContent='center'
            marginTop='1rem'
            onClick={() => {
              window.dataLayer.push({ event: GA_EVENTS.SIGN_UP, method: 'Manual' });
            }}
            padding='.5rem 0'
            transition='all 0.3s ease-in'
            w='100%'
          >
            <Text
              fontSize={DESIGN_CONFIG.typography.common.webButton.fontSize}
              fontWeight={DESIGN_CONFIG.typography.common.webButton.fontWeight}
              marginBottom='0'
              textColor={DESIGN_CONFIG.color.neutral.neutral500}
            >
              {loading ? <CircularProgress size={18} color={DESIGN_CONFIG.color.neutral.neutral500} /> : STRING_CONFIG.inPage.common.txtSignUp}
            </Text>
          </Button>
        </Form>
      )}
    </Formik>
  );
}

export default SignUp;
